import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/client';

/**
 * Emit page_view events to /api/activity/events on every route change.
 *
 * Batched + flushed every 3 seconds or on unload, so a burst of quick
 * navigations doesn't spam the API. Fails silently — logging must never
 * interfere with navigation.
 */
const FLUSH_MS = 3000;
const MAX_BUFFER = 25;
const queue = [];
let timer = null;

function flush() {
  if (queue.length === 0) return;
  const events = queue.splice(0);
  api.post('/activity/events', { events }).catch(() => { /* silent */ });
}

function enqueue(event) {
  queue.push({
    ...event,
    metadata: { ...event.metadata, client_time: new Date().toISOString() },
  });
  if (queue.length >= MAX_BUFFER) {
    flush();
    return;
  }
  if (!timer) {
    timer = setTimeout(() => { timer = null; flush(); }, FLUSH_MS);
  }
}

// Flush on tab close / reload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => { if (queue.length) flush(); });
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && queue.length) flush();
  });
}

/**
 * Mount-level hook. Drops a page_view every time the route path changes.
 * Use in Layout so it runs once per authenticated session.
 */
export function useActivityTracking() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === lastPath.current) return;
    lastPath.current = path;
    enqueue({
      event_type: 'page_view',
      event_name: `page.${location.pathname}`,
      path,
      metadata: { search: location.search || null },
    });
  }, [location]);
}

/**
 * Direct call for ad-hoc UI events — button clicks, modal opens, bulk
 * action fires. Use sparingly — only for interactions that matter for
 * audit / UX analytics.
 */
export function trackUiEvent(name, { entityType = null, entityId = null, metadata = null } = {}) {
  enqueue({
    event_type: 'ui_event',
    event_name: name,
    entity_type: entityType,
    entity_id: entityId,
    metadata,
  });
}
